import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = { name: "", company_name: "", contact_name: "", phone: "", email: "", address: "", status: "active", notes: "" };

export default function ClientsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/clients");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setErr(""); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setErr(""); setModalOpen(true); };
  const close = () => { setModalOpen(false); };

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      close(); await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    await api.delete(`/clients/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="lx-overline">Catálogo</div>
          <h1 className="lx-section-title mt-1">Clientes</h1>
          <p className="text-lx-muted text-sm mt-1">Gestiona las empresas a las que prestas servicio.</p>
        </div>
        <button className="lx-btn lx-btn-primary" onClick={openNew} data-testid="new-client-button">
          <Plus size={16}/> Nuevo cliente
        </button>
      </div>

      <div className="lx-card p-0 overflow-hidden">
        {loading ? <div className="p-6 text-lx-muted">Cargando…</div> : items.length === 0 ? (
          <div className="p-10 text-center text-lx-muted">
            <p>Aún no tienes clientes registrados.</p>
            <button className="lx-btn lx-btn-primary mt-4" onClick={openNew}>Crear el primero</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lx-table">
              <thead>
                <tr><th>Cliente</th><th>Contacto</th><th>Teléfono</th><th>Email</th><th>Estatus</th><th className="text-right">Acciones</th></tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`}>
                    <td>
                      <div className="font-medium">{c.name}</div>
                      {c.company_name && <div className="text-xs text-lx-muted">{c.company_name}</div>}
                    </td>
                    <td>{c.contact_name || "—"}</td>
                    <td>{c.phone || "—"}</td>
                    <td>{c.email || "—"}</td>
                    <td>
                      <span className={`lx-badge ${c.status === "active" ? "lx-badge-success" : "lx-badge-muted"}`}>
                        {c.status === "active" ? "Activo" : c.status === "paused" ? "Pausado" : "Inactivo"}
                      </span>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button className="lx-btn lx-btn-ghost" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil size={14}/></button>
                      <button className="lx-btn lx-btn-ghost" onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal title={editing ? "Editar cliente" : "Nuevo cliente"} onClose={close}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nombre" required value={form.name} onChange={(v)=>setForm({...form, name: v})} testid="client-name"/>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Empresa" value={form.company_name} onChange={(v)=>setForm({...form, company_name: v})} testid="client-company"/>
              <Field label="Contacto principal" value={form.contact_name} onChange={(v)=>setForm({...form, contact_name: v})} testid="client-contact"/>
              <Field label="Teléfono" value={form.phone} onChange={(v)=>setForm({...form, phone: v})} testid="client-phone"/>
              <Field label="Correo" type="email" value={form.email} onChange={(v)=>setForm({...form, email: v})} testid="client-email"/>
            </div>
            <Field label="Dirección" value={form.address} onChange={(v)=>setForm({...form, address: v})} testid="client-address"/>
            <div>
              <label className="lx-label">Estatus</label>
              <select className="lx-input" value={form.status} onChange={(e)=>setForm({...form, status: e.target.value})} data-testid="client-status">
                <option value="active">Activo</option>
                <option value="paused">Pausado</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
            <div>
              <label className="lx-label">Notas internas</label>
              <textarea className="lx-input" rows={3} value={form.notes || ""} onChange={(e)=>setForm({...form, notes: e.target.value})} data-testid="client-notes"/>
            </div>
            {err && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="lx-btn lx-btn-secondary" onClick={close}>Cancelar</button>
              <button type="submit" className="lx-btn lx-btn-primary" disabled={saving} data-testid="save-client-button">
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children, maxWidth = "max-w-xl" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="modal-backdrop">
      <div className={`bg-white rounded-xl shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto`} data-testid="modal-content">
        <div className="flex items-center justify-between px-5 py-4 border-b border-lx-border sticky top-0 bg-white z-10">
          <h3 className="font-heading font-bold text-lg">{title}</h3>
          <button className="p-1.5 rounded hover:bg-lx-surface" onClick={onClose} data-testid="modal-close"><X size={18}/></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="lx-label">{label}{required && <span className="text-lx-error"> *</span>}</label>
      <input
        className="lx-input"
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
